import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useParams, Navigate } from 'react-router-dom'
import { X, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react'

import Meta from '../components/common/Meta'
import Sectionbreak from '../components/Sectionbreak'
import { projects } from '../constants/projects'
import AnimatedSection from '../components/common/AnimatedSection'
import { ProjectSummary } from '../components/ProjectSummary'

function ProjectDetails() {
  const { slug } = useParams()
  const project = projects.find((p) => p.slug === slug)

  const [selectedImageIndex, setSelectedImageIndex] = useState(null)


  const {
    title,
    coverImage,
    images,
    shortDescription,
    description,
    highlights,
    specifications,
    servicesProvided,
    location,
    date,
    duration,
    area,
    client,
    seo,
  } = project

  useEffect(() => {
    const handleKey = (e) => {
      if (selectedImageIndex === null) return

      if (e.key === 'ArrowLeft' && selectedImageIndex > 0) {
        setSelectedImageIndex((i) => i - 1)
      }
      if (e.key === 'ArrowRight' && selectedImageIndex < images.length - 1) {
        setSelectedImageIndex((i) => i + 1)
      }
      if (e.key === 'Escape') {
        setSelectedImageIndex(null)
      }
    }

    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [selectedImageIndex, images.length])

    if (!project) {
        return <Navigate to="/projects" replace />
    }

  return (
    <>
      {/* SEO */}
      <Meta title={seo?.title || title} description={seo?.description} />

      {/* HERO */}
      <header
        className="relative min-h-[70vh] bg-cover bg-center flex items-center"
        style={{ backgroundImage: `url(${coverImage})` }}
      >
        <div className="absolute inset-0 bg-black/65" />
      </header>

      {/* OVERVIEW */}
      <AnimatedSection className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12">
          <div>
            <Sectionbreak
              title="Project"
              highlight="Overview"
              description={description}
              className="text-left"
            />

            {/* Services Provided */}
            <div>
            {servicesProvided?.length > 0 && (
            <div className="mt-4 text-center">
                <h1 className="text-xl mb-4 font-semibold text-gray-600">
                Services Provided
                </h1>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-3">
                {servicesProvided.map((service, i) => (
                    <div
                    key={i}
                    className="flex items-center gap-2 
                        bg-gray-50 border border-gray-100 
                        px-4 py-2 rounded-lg 
                        text-sm text-gray-700 cursor-pointer
                        hover:bg-teal-50 transition"
                    >
                    <span className="w-2 h-2 rounded-full bg-teal-800"></span>
                    <span className='text-lg font-extralight'>{service}</span>
                    </div>
                ))}
                </div>
            </div>
            )}
        </div>
        </div>

          {/* PROJECT SUMMARY */}
            <ProjectSummary
              title={title}
              description={shortDescription}
              client={client}
              location={location}
              date={date}
              duration={duration}
              area={area}
            />
        </div>
      </AnimatedSection>

      {/* GALLERY */}
      <AnimatedSection className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <Sectionbreak
            title="Project"
            highlight="Gallery"
            description="A visual journey through the project."
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-10">
            {images.map((img, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                onClick={() => setSelectedImageIndex(index)}
                className="group cursor-pointer relative overflow-hidden rounded-2xl shadow-md"
              >
                <img
                  src={img}
                  alt={`${title} ${index + 1}`}
                  className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                  <span className="text-white text-sm uppercase tracking-wide">
                    View Image
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* HIGHLIGHTS & SPECS */}
      <AnimatedSection className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12">
          {/* Highlights */}
          <div>
            <h3 className="text-xl font-semibold mb-6 text-gray-500">Key Highlights</h3>
            <ul className="space-y-4">
              {highlights.map((item, index) => (
                <li key={index} className="flex gap-3">
                  <CheckCircle className="text-teal-600 w-5 h-5 mt-1" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Specifications */}
          {specifications?.length > 0 && (
            <div className="bg-gray-50 p-8 rounded-xl shadow-sm">
              <h3 className="text-xl font-semibold mb-6 text-gray-500">
                Technical Specifications
              </h3>
              <ul className="space-y-3 text-sm">
                {specifications.map((spec, i) => (
                  <li key={i} className="flex justify-between border-b pb-2">
                    <span className="text-gray-600">{spec.label}</span>
                    <span className="font-medium">{spec.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </AnimatedSection>

      {/* CTA */}
      <AnimatedSection className="py-16 bg-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Interested in a Similar Project?
          </h2>
          <p className="mb-6 text-teal-100">
            Let’s discuss how we can bring your vision to life.
          </p>
          <a
            href="/contact"
            className="inline-block bg-white text-teal-700 px-10 py-3 rounded-md font-semibold hover:bg-gray-100 transition"
          >
            Contact Us
          </a>
        </div>
      </AnimatedSection>

      {/* IMAGE MODAL */}
      {selectedImageIndex !== null && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center px-4"
        >
          <button
            onClick={() => setSelectedImageIndex(null)}
            className="absolute top-6 right-6 text-white hover:text-gray-300"
          >
            <X size={32} />
          </button>

          {selectedImageIndex > 0 && (
            <button
              onClick={() => setSelectedImageIndex((i) => i - 1)}
              className="absolute left-6 text-white hover:text-gray-300"
            >
              <ChevronLeft size={40} />
            </button>
          )}

          {selectedImageIndex < images.length - 1 && (
            <button
              onClick={() => setSelectedImageIndex((i) => i + 1)}
              className="absolute right-6 text-white hover:text-gray-300"
            >
              <ChevronRight size={40} />
            </button>
          )}

          <motion.img
            src={images[selectedImageIndex]}
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="max-w-full max-h-[85vh] rounded-xl shadow-2xl object-contain"
          />
        </motion.div>
      )}
    </>
  )
}

export default ProjectDetails
